import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { useSiteContent } from '../context/SiteContentContext';
import { DEFAULT_LOGO } from '../siteContent';

const navLinks = [
  { name: 'Home', path: '/' },
  { name: 'About', path: '/about' },
  { name: 'How We Help', path: '/how-we-help' },
  { name: 'Knowledge Hub', path: '/knowledge-hub' },
  { name: 'Corporate Life Series', path: '/corporate-life-series' },
  { name: 'Events', path: '/events' },
  { name: 'Contact', path: '/contact' },
  { name: 'Donate', path: '/donate' }
];

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { home } = useSiteContent();
  const logo = home?.logo || DEFAULT_LOGO;

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => { setIsMobileMenuOpen(false); }, [location]);

  return (
    <nav
      className={`sticky top-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white/90 backdrop-blur-xl shadow-[0_2px_20px_rgba(0,0,0,0.06)]' : 'bg-[#FDFBF9]'
      }`}
      data-testid="main-navbar"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center" data-testid="nav-logo-link">
            <img
              src={logo}
              alt="Equaltoo Foundation"
              className="h-14 w-auto"
            />
          </Link>

          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.path}
                className={`text-sm font-medium transition-colors hover:text-[#E74C3C] ${
                  location.pathname === link.path ? 'text-[#E74C3C]' : 'text-[#1A1A1A]'
                }`}
                data-testid={`nav-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          <div className="hidden lg:flex items-center gap-3">
            <Link to="/login" data-testid="nav-login-btn">
              <Button variant="ghost" className="rounded-full text-[#003366] hover:bg-[#003366]/5 font-medium">
                Login
              </Button>
            </Link>
            <Link to="/membership" data-testid="nav-join-btn">
              <Button className="rounded-full bg-[#E74C3C] hover:bg-[#C0392B] text-white px-6 font-medium transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_8px_30px_rgba(231,76,60,0.35)] group">
                Join Free
                <ArrowRight className="ml-1.5 w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </Button>
            </Link>
          </div>

          <button
            className="lg:hidden p-2 text-[#003366]"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {isMobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-[#EAE5E0]" data-testid="mobile-menu">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.path}
                className="block py-3 text-[#1A1A1A] hover:text-[#E74C3C] font-medium transition-colors"
              >
                {link.name}
              </Link>
            ))}
            <div className="flex gap-3 pt-4">
              <Link to="/login" className="flex-1">
                <Button variant="outline" className="w-full rounded-full border-[#003366] text-[#003366]">Login</Button>
              </Link>
              <Link to="/membership" className="flex-1">
                <Button className="w-full rounded-full bg-[#E74C3C] hover:bg-[#C0392B] text-white">Join Free</Button>
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
